import { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { searchMatches } from '../../../../../functions/intranet/src/lib/textFold.js'
import { ModalContext } from './Modal'
import { registerMenuEscape } from './overlayStack'
import { floatPlatz, optionNach } from './dropdownFloat'

export interface DropdownOption {
  value: string
  label: string
  /** Optional secondary line shown beneath the label inside the menu and
   *  searched alongside the label when `searchable` is true. */
  description?: string
  /** Zusätzlicher Suchtext, etwa vollständig durchsuchbare, gekürzt angezeigte Kennungen. */
  searchText?: string
  /** Optional short badge rendered beside the label inside the menu.
   *  Not included in search matching. */
  note?: string
  /** Optional element before the label — menu rows AND the trigger (e.g.
   *  <CountryFlags> next to a language). Not included in search matching. */
  leading?: ReactNode
}

/** A labeled section of options for grouped menu rendering — see `optionGroups`. */
export interface DropdownOptionGroup {
  /** Section header; '' renders the section without one. */
  label: string
  options: DropdownOption[]
  /** Tints this group's rows to call out a recommended/matching subset. */
  highlight?: boolean
}

interface DropdownProps {
  /** Optional UPPERCASE-tracked label above the trigger. Omit for inline use (e.g. table filters). */
  label?: string
  /** Currently selected value. */
  value: string
  /** String[] (label = value) OR DropdownOption[] for label/value separation. */
  options: readonly string[] | readonly DropdownOption[]
  onChange: (v: string) => void
  /** Placeholder shown when no value selected. */
  placeholder?: string
  /** Compact size — for inline filters next to text. Default = full size for forms.
   *  `touch` — an unmistakable field for people on phones/tablets (kitchen staff): min. 44 px high,
   *  clearly bordered, bigger chevron and menu rows, visible focus ring. */
  size?: 'default' | 'compact' | 'touch'
  /** Material Symbols name shown in front of the trigger's content (e.g. `translate` for a language choice). */
  icon?: string
  /** Accessible name of the trigger when there is no visible `label` (the button's text is only the current value). */
  ariaLabel?: string
  disabled?: boolean
  /** The field failed validation: error border + `aria-invalid` on the trigger (surface variant; the page shows the message). */
  invalid?: boolean
  /** Min/max width of the open menu (in px). */
  menuMinWidth?: number
  /** `surface` (default) on flat pages; `glass` for Liquid Glass surfaces. */
  variant?: 'surface' | 'glass'
  /** Render the menu via React Portal to document.body. Set TRUE when the
   *  Dropdown lives inside a parent with overflow:hidden / overflow:auto so
   *  the menu can escape the clipping boundary. The trigger stays in flow;
   *  only the popup is teleported and positioned with fixed coordinates.
   *  Defaults to `true` automatically inside a <Modal> (its scroll container
   *  clips an in-flow menu) — pass this explicitly to override that default. */
  usePortal?: boolean
  /** Anchor edge of the menu relative to the trigger.
   *  'left' (default) → menu's left edge aligns with trigger's left edge,
   *    grows rightward (good when the menu is on the LEFT of the page).
   *  'right' → menu's right edge aligns with trigger's right edge,
   *    grows leftward (good when the menu is on the RIGHT of the page or
   *    inside a row with long option labels — prevents overflow). */
  align?: 'left' | 'right'
  /** Show a typeable search input at the top of the menu and filter the
   *  options list by case-insensitive substring match. Use for ≥10 options. */
  searchable?: boolean
  /** Override the portal menu's stacking order. Default 1000, or 90 when the
   *  Dropdown defaults to portal mode inside a <Modal> (see `usePortal`) —
   *  comfortably above the Modal's own z (70 + 2 per stacked depth). */
  menuZIndex?: number
  /** Render options as labeled, divided sections instead of one flat list.
   *  `options` must still be passed (flattened) — it drives value lookup and
   *  the search-fallback list. Groups only render while no search query is
   *  active; typing a query falls back to the flat filtered list. Omit for
   *  today's flat-list behaviour, unchanged. */
  optionGroups?: DropdownOptionGroup[]
  /** Look of the open menu. 'default' = today's flat list. 'float' = the round glass panel of the school portal (prototype .float/.opt): 20 px radius,
   *  8 px padding, 12 px rounded 48 px rows, the selected row filled with the primary colour, bold, with a check icon; exactly as wide as the trigger;
   *  opens upward when the rows do not fit below (and, on the plain page, keeps clear of an element marked data-menu-avoid, e.g. a phone's bottom bar); the focus moves
   *  into the list on open, Up/Down/Home/End move through the rows, Tab and Escape close and give the focus back to the trigger. It always renders
   *  through a portal. Put data-menu-field on the box around label and trigger so an upward menu stays clear of the label. */
  menuLook?: 'default' | 'float'
  /** Leave the row with the empty value ('') out of the open list; the trigger still shows the label of the selected '' row (e.g. "Bitte wählen"). */
  menuOmitEmpty?: boolean
}

function normalize(options: DropdownProps['options']): DropdownOption[] {
  return options.map(o => typeof o === 'string' ? { value: o, label: o } : o)
}

/**
 * The single canonical Dropdown for the intranet.
 * NEVER use native <select>. See DESIGN.md § 8c.
 */
export function Dropdown({
  label, value, options, onChange,
  placeholder = 'Bitte wählen…',
  size = 'default',
  icon,
  ariaLabel,
  disabled = false,
  invalid = false,
  menuMinWidth,
  variant = 'surface',
  usePortal,
  align = 'left',
  searchable = false,
  menuZIndex,
  optionGroups,
  menuLook = 'default',
  menuOmitEmpty = false,
}: DropdownProps) {
  const insideModal = useContext(ModalContext)
  const float = menuLook === 'float'
  const effectiveUsePortal = float || (usePortal ?? insideModal)
  const effectiveMenuZIndex = menuZIndex ?? (insideModal ? 90 : 1000)
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const searchInputRef = useRef<HTMLInputElement | null>(null)
  const listRef = useRef<HTMLDivElement | null>(null)
  // `top` XOR `bottom`: the menu anchors below the trigger normally, and flips
  // to anchor ABOVE it when there isn't room below. maxHeight is always the
  // space actually available in that direction, so the menu can never run off
  // the viewport — previously it always opened downward with a fixed max-h-72
  // and got clipped by the page edge, with no way to scroll to the rest.
  const [menuRect, setMenuRect] = useState<
    | {
        top?: number
        bottom?: number
        left?: number
        right?: number
        width: number
        maxHeight: number
      }
    | null
  >(null)
  const opts = normalize(options)
  const current = opts.find(o => o.value === value)

  /** Closes the menu. The float menu moves the focus into its list, so it gives the focus back to the trigger (prototype floatClose). */
  const closeMenu = (refocus: boolean) => {
    setIsOpen(false)
    if (refocus && float) triggerRef.current?.focus({ preventScroll: true })
  }
  // Escape is closed by the overlay stack (registerMenuEscape below) — never here, or the Modal around the menu would see it on top. The float menu only
  // notes that the key came from inside its list; when the menu is gone the focus goes back to the trigger.
  const escapeAusListe = useRef(false)
  useEffect(() => {
    if (isOpen || !escapeAusListe.current) return
    escapeAusListe.current = false
    triggerRef.current?.focus({ preventScroll: true })
  }, [isOpen])

  // Filter options by search query — folded so accents/ß-vs-ss don't hide
  // matches (e.g. 'strasse' finds 'Straße'). When an option has a
  // `description`, that's searched alongside the label.
  const filteredOpts = useMemo<DropdownOption[]>(() => {
    const listed = menuOmitEmpty ? opts.filter(o => o.value !== '') : opts
    if (!searchable || !searchQuery.trim()) return listed
    const q = searchQuery.trim()
    return listed.filter((o) => {
      if (searchMatches(o.label, q)) return true
      if (o.description && searchMatches(o.description, q)) return true
      if (o.searchText && searchMatches(o.searchText, q)) return true
      return false
    })
  }, [opts, searchable, searchQuery, menuOmitEmpty])

  // Grouped rendering only applies while there's no active search query —
  // typing a query always falls back to the flat filtered list above.
  const showGroups = !!optionGroups && (!searchable || !searchQuery.trim())

  // Reset + autofocus search input on open.
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('')
      return
    }
    if (searchable) {
      // Wait a tick for the menu to mount before focusing.
      const t = setTimeout(() => searchInputRef.current?.focus(), 0)
      return () => clearTimeout(t)
    }
  }, [isOpen, searchable])

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node
      const insideWrap = wrapRef.current?.contains(target)
      const insideMenu = menuRef.current?.contains(target)
      if (!insideWrap && !insideMenu) setIsOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [isOpen])

  // Escape schließt NUR das Menü, nie den Drawer/das Modal darum (overlayStack: das offene Menü steht obenauf).
  useEffect(() => (isOpen ? registerMenuEscape(() => setIsOpen(false)) : undefined), [isOpen])

  // Schloss das Menü, während der Fokus darin stand (Escape, Wahl per Tastatur), ist der Eintrag weg und der Fokus liegt auf
  // body: er geht an den Auslöser zurück. Steht er schon auf einem anderen Element (Klick daneben), bleibt er dort.
  const focusWasInMenu = useRef(false)
  useEffect(() => {
    if (isOpen) return
    if (focusWasInMenu.current && (!document.activeElement || document.activeElement === document.body)) triggerRef.current?.focus()
    focusWasInMenu.current = false
  }, [isOpen])

  // Tastatur: Pfeil runter/hoch am Auslöser öffnet das Menü bzw. springt in die Einträge (das Portal-Menü liegt am Ende
  // von <body> und ist per Tab nicht erreichbar); in den Einträgen laufen Pfeile/Pos1/Ende durch die Liste.
  const optionButtons = () => Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('button') ?? [])
  const focusOption = (last: boolean) => {
    const items = optionButtons()
    ;(items.find(b => b.getAttribute('aria-current') === 'true') ?? items[last ? items.length - 1 : 0])?.focus()
  }
  const focusAfterOpen = useRef<'first' | 'last' | null>(null)
  const onTriggerKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
    e.preventDefault()
    if (isOpen) focusOption(e.key === 'ArrowUp')
    else { focusAfterOpen.current = e.key === 'ArrowUp' ? 'last' : 'first'; setIsOpen(true) }
  }
  // Opened by arrow key: move into the list as soon as it is mounted (portal: as soon as it is positioned and visible).
  useEffect(() => {
    if (!isOpen || !focusAfterOpen.current || (effectiveUsePortal && !menuRect)) return
    focusOption(focusAfterOpen.current === 'last')
    focusAfterOpen.current = null
  })
  const onMenuKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.target instanceof HTMLInputElement && e.key === 'ArrowDown') { e.preventDefault(); focusOption(false); return }
    if (!(e.target instanceof HTMLButtonElement)) return   // the search input keeps Pos1/Ende for its caret
    const items = optionButtons()
    const i = items.indexOf(e.target)
    const to = e.key === 'ArrowDown' ? Math.min(i + 1, items.length - 1) : e.key === 'ArrowUp' ? Math.max(i - 1, 0) : e.key === 'Home' ? 0 : e.key === 'End' ? items.length - 1 : -1
    if (to < 0 || i < 0) return
    e.preventDefault()
    items[to]?.focus()
  }

  // Position the portal menu relative to the trigger. Re-measure on open +
  // window resize/scroll so the menu stays anchored.
  // Float menu: the direction (up/down) is chosen once per opening; a scroll only moves the menu, it never flips it.
  const floatRichtung = useRef<boolean | null>(null)
  useLayoutEffect(() => {
    if (!isOpen || !effectiveUsePortal) return
    floatRichtung.current = null
    const measure = () => {
      const trigger = triggerRef.current
      if (!trigger) return
      const r = trigger.getBoundingClientRect()
      if (float) {
        // The float menu: as wide as the trigger, below it or (no room) above the whole field, never behind a bar marked data-menu-avoid.
        let grenzeUnten = window.innerHeight
        // Inside a Modal the backdrop covers such a bar, so only a menu on the plain page keeps clear of it.
        if (!insideModal) {
          document.querySelectorAll<HTMLElement>('[data-menu-avoid]').forEach((el) => {
            if (el.getClientRects().length === 0) return
            const oben = el.getBoundingClientRect().top
            if (oben > r.top && oben < grenzeUnten) grenzeUnten = oben
          })
        }
        const liste = listRef.current
        const schale = menuRef.current
        // Natural height = the shell without its list + the whole list (scrollHeight is the full height even while the list scrolls).
        const hoehe = liste && schale ? schale.scrollHeight - liste.clientHeight + liste.scrollHeight : 0
        const feld = trigger.closest('[data-menu-field]') ?? trigger
        const p = floatPlatz({
          trigger: { left: r.left, bottom: r.bottom, width: r.width }, feldTop: feld.getBoundingClientRect().top, grenzeUnten,
          fensterBreite: window.innerWidth, fensterHoehe: window.innerHeight, hoehe, nachObenFest: floatRichtung.current ?? undefined,
        })
        floatRichtung.current = p.nachOben
        setMenuRect({ left: p.left, width: p.width, maxHeight: p.maxHeight, ...(p.top !== undefined ? { top: p.top } : {}), ...(p.bottom !== undefined ? { bottom: p.bottom } : {}) })
        return
      }
      const GAP = 4
      const EDGE = 8                       // keep clear of the viewport edge
      const spaceBelow = window.innerHeight - r.bottom - GAP - EDGE
      const spaceAbove = r.top - GAP - EDGE
      // Flip up only when below genuinely can't hold a usable menu AND above is
      // roomier. Never flip for a couple of pixels — that would make the menu
      // jump around while scrolling.
      const flipUp = spaceBelow < 180 && spaceAbove > spaceBelow
      const maxHeight = Math.max(120, Math.floor(flipUp ? spaceAbove : spaceBelow))

      // Horizontal anchor is unchanged: 'right' grows leftward, else rightward.
      const horizontal = align === 'right'
        ? { right: window.innerWidth - r.right }
        : { left: r.left }

      setMenuRect({
        ...horizontal,
        ...(flipUp
          ? { bottom: window.innerHeight - r.top + GAP }
          : { top: r.bottom + GAP }),
        width: r.width,
        maxHeight,
      })
    }
    measure()
    window.addEventListener('resize', measure)
    window.addEventListener('scroll', measure, true) // capture nested scrolls
    return () => {
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure, true)
    }
  }, [isOpen, effectiveUsePortal, align, float, insideModal])

  // Float menu: once it has its place the focus moves onto the selected row (or the first), like the prototype, so the trigger shows no second ring.
  useEffect(() => {
    if (!isOpen || !float || !menuRect) return
    const zeile = menuRef.current?.querySelector<HTMLElement>('[role="option"][aria-selected="true"]') ?? menuRef.current?.querySelector<HTMLElement>('[role="option"]')
    if (!zeile || menuRef.current?.contains(document.activeElement)) return
    zeile.focus({ preventScroll: true })
    zeile.scrollIntoView({ block: 'nearest' })
  }, [isOpen, float, menuRect])

  /** Up/Down/Home/End move through the rows of the float menu; Tab closes it and gives the focus back to the trigger. */
  const menuTaste = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Tab') { e.preventDefault(); closeMenu(true); return }
    if (e.code === 'Escape') { escapeAusListe.current = true; return }
    const zeilen = Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="option"]') ?? [])
    const ziel = optionNach(e.key, zeilen.indexOf(document.activeElement as HTMLElement), zeilen.length)
    if (ziel < 0) return
    e.preventDefault()
    e.stopPropagation()
    zeilen[ziel].focus({ preventScroll: true })
    zeilen[ziel].scrollIntoView({ block: 'nearest' })
  }

  /** Arrow keys on the float trigger: open the menu (the focus then moves into it), or step into the list when it is already open. */
  const triggerTaste = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
    e.preventDefault()
    if (!isOpen) { setIsOpen(true); return }
    const zeilen = Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="option"]') ?? [])
    const ziel = e.key === 'ArrowUp' ? zeilen[zeilen.length - 1] : zeilen.find(z => z.getAttribute('aria-selected') === 'true') ?? zeilen[0]
    ziel?.focus({ preventScroll: true })
    ziel?.scrollIntoView({ block: 'nearest' })
  }

  // Sichtbarer Tastaturfokus am Auslöser (nur :focus-visible, die Mausbedienung sieht keinen Ring).
  const focusRing = 'focus-visible:ring-2 focus-visible:ring-primary/50'
  const surfaceTrigger = size === 'compact'
    ? `w-full bg-surface-container-low text-on-surface border px-2.5 py-1.5 rounded-lg text-xs flex items-center justify-between gap-2 transition-colors outline-none ${focusRing} ${
        isOpen ? 'border-primary' : invalid ? 'border-error/60' : 'border-outline-variant hover:border-outline'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`
    : `w-full bg-surface-container-low text-on-surface border px-3 py-2.5 rounded-xl text-sm flex items-center justify-between gap-2 transition-all outline-none ${focusRing} ${
        isOpen ? 'border-primary ring-4 ring-primary/5 bg-surface-container' : invalid ? 'border-error/60 ring-1 ring-error/20' : 'border-outline-variant hover:border-primary/30'
      } ${disabled ? 'opacity-50 cursor-not-allowed bg-surface-container' : ''}`
  const glassTrigger = size === 'compact'
    ? `w-full bg-white/50 dark:bg-slate-900/45 backdrop-blur border px-2.5 py-1.5 rounded-lg text-xs flex items-center justify-between gap-2 transition-colors outline-none ${focusRing} ${
        isOpen ? 'border-primary/60 bg-white/85 dark:bg-slate-900/75' : 'border-white/50 dark:border-white/15 hover:bg-white/65 dark:hover:bg-slate-900/60'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`
    : `w-full bg-white/50 dark:bg-slate-900/45 backdrop-blur border px-3 py-2.5 rounded-xl text-sm flex items-center justify-between gap-2 transition-all outline-none ${focusRing} ${
        isOpen ? 'border-primary/60 ring-4 ring-primary/10 bg-white/85' : 'border-white/50 dark:border-white/15 hover:bg-white/65 dark:hover:bg-slate-900/60'
      } ${disabled ? 'opacity-50 cursor-not-allowed bg-white/40 dark:bg-slate-900/40' : ''}`
  // Touch: eigene, deutlich sichtbare Feldform — gefüllt, kräftiger Rand, 44 px hoch, Hover- und Fokuszustand.
  const touchTrigger = `w-full min-h-[44px] bg-surface-container-low text-on-surface border-2 px-3 py-2 rounded-xl text-sm font-medium shadow-sm flex items-center justify-between gap-2 transition-colors outline-none focus-visible:ring-4 focus-visible:ring-primary/25 ${
    isOpen ? 'border-primary bg-surface-container' : 'border-outline hover:border-primary/60 hover:bg-surface-container'
  } ${disabled ? 'opacity-50 cursor-not-allowed hover:border-outline hover:bg-surface-container-low' : ''}`
  const triggerCls = size === 'touch' ? touchTrigger : variant === 'glass' ? glassTrigger : surfaceTrigger

  // inline-flex (instead of flex) is critical with width:max-content — it
  // gives the shell a shrink-to-fit intrinsic size, breaking the circular
  // dependency between the parent's max-content sizing and any percentage-
  // width descendants (e.g. the search input with w-full). items-start
  // prevents flex stretch from filling the cross-axis to viewport width.
  // The menu must be OPAQUE — it floats (often portaled) over other content, so a
  // translucent glass panel lets that content bleed through and become unreadable.
  // The glass `variant` still styles the trigger; the menu is always solid.
  const menuShellClass = float
    // The prototype .float: 20 px radius, 8 px padding, 96 % card colour with a blur, a 1 px outline ring and a soft shadow (own light/dark shadow).
    ? 'dd-float flex flex-col rounded-[20px] p-2 overflow-hidden text-on-surface bg-[color-mix(in_srgb,var(--color-card)_96%,transparent)] backdrop-blur-[24px] backdrop-saturate-[1.8] shadow-[inset_0_1px_0_rgba(255,255,255,.7),inset_0_0_0_1px_var(--color-outline-variant),0_24px_50px_-12px_rgba(15,23,42,.28)] dark:shadow-[inset_0_1px_0_rgba(255,255,255,.12),inset_0_0_0_1px_var(--color-outline-variant),0_24px_50px_-12px_rgba(0,0,0,.5)]'
    : 'rounded-lg shadow-lg overflow-hidden inline-flex flex-col items-start bg-surface-container-lowest border border-outline-variant'

  // Render a single option row — shared between the flat and grouped paths.
  // `highlight` tints the row for options inside a `highlight: true` group.
  const renderOptionButton = (o: DropdownOption, highlight = false) => float ? (
    // The prototype .opt: 48 px, 12 px radius, hover = glass, selected = primary fill, bold, check icon on the right.
    <button
      key={o.value}
      type="button"
      role="option"
      aria-selected={o.value === value}
      tabIndex={-1}
      onClick={() => { onChange(o.value); closeMenu(true) }}
      className={`flex min-h-12 w-full items-center justify-between gap-2.5 rounded-xl px-3 py-2 text-left text-base outline-hidden focus-visible:outline-solid focus-visible:outline-3 focus-visible:outline-primary focus-visible:outline-offset-2 transition-colors ${
        o.value === value ? 'bg-primary font-bold text-on-primary' : 'bg-transparent font-medium text-on-surface hover:bg-glass-a'
      }`}
    >
      {/* T12: like the prototype .opt a long label (name + role) wraps onto a second line at 390 px instead of being cut off; words are not split. */}
      <span className="min-w-0 [overflow-wrap:break-word]">{o.label}</span>
      {o.value === value && <span className="material-symbols-outlined shrink-0 !text-[20px]" aria-hidden="true">check</span>}
    </button>
  ) : (
    <button
      key={o.value}
      type="button"
      aria-current={o.value === value ? 'true' : undefined}
      onClick={() => { onChange(o.value); setIsOpen(false) }}
      className={`block w-full text-left px-3 ${size === 'touch' ? 'py-3 text-sm' : 'py-2 text-xs'} font-medium transition-colors ${o.description ? '' : 'whitespace-nowrap'} ${
        o.value === value
          ? variant === 'glass' ? 'bg-white/70 dark:bg-slate-800/75 text-primary' : 'bg-primary/5 text-primary'
          : variant === 'glass'
            ? `text-on-surface hover:bg-white/50 dark:hover:bg-slate-800/60 ${highlight ? 'bg-primary/5' : ''}`
            : `text-on-surface hover:bg-surface-container-low ${highlight ? 'bg-primary/5' : ''}`
      }`}
    >
      <span className="flex items-center gap-1.5 min-w-0">
        {o.leading ? <span className="shrink-0 inline-flex items-center">{o.leading}</span> : null}
        <span className="truncate min-w-0">{o.label}</span>
        {o.note && (
          <span className="shrink-0 px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide bg-primary/10 text-primary">
            {o.note}
          </span>
        )}
      </span>
      {o.description && (
        <span className="block text-[10px] text-on-surface-variant font-normal mt-0.5 truncate">
          {o.description}
        </span>
      )}
    </button>
  )

  // Grouped rendering: uppercase section headers + a divider between groups.
  // Only reached while showGroups is true (see above).
  const renderGroupedOptions = () => {
    const groups = (optionGroups ?? []).filter(g => g.options.length > 0)
    if (groups.length === 0) {
      return (
        <div className="px-3 py-3 text-xs text-on-surface-variant text-center italic">
          Keine Treffer
        </div>
      )
    }
    return groups.map((g, i) => (
      <div key={g.label || i} className={i > 0 ? 'border-t border-slate-200 dark:border-white/10' : ''}>
        {/* Empty label = unlabeled section (e.g. a lone "Alle" entry on top) */}
        {g.label && (
          <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-on-surface-variant">
            {g.label}
          </div>
        )}
        {g.options.map(o => renderOptionButton(o, g.highlight))}
      </div>
    ))
  }

  // Render the option list (and optional search input) — shared between the
  // in-flow and portal paths.
  const renderOptionList = () => (
    <>
      {searchable && (
        <div className="self-stretch border-b border-slate-200 dark:border-white/10 p-1.5 min-w-0">
          <div className="relative">
            <span
              className="material-symbols-outlined absolute left-2 top-1/2 -translate-y-1/2 text-on-surface-variant pointer-events-none"
              style={{ fontSize: 14 }}
              aria-hidden="true"
            >
              search
            </span>
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Suchen…"
              // Chrome's address/profile autofill ("onur" → Adressen
              // verwalten…) pops over this input inside a Modal — plain
              // autoComplete="off" is a known no-op for name/address-shaped
              // fields, so "one-time-code" is used instead: an OTP-only
              // semantic Chrome won't match against saved addresses/profiles.
              // data-lpignore/data-1p-ignore opt this field out of LastPass/
              // 1Password suggestion overlays the same way.
              autoComplete="one-time-code"
              name="ui-search"
              data-lpignore="true"
              data-1p-ignore="true"
              className="w-0 min-w-full bg-surface-container-low border border-outline-variant rounded-md pl-7 pr-2 py-1 text-xs text-on-surface placeholder:text-on-surface-variant focus:outline-none focus:border-primary"
            />
          </div>
        </div>
      )}
      <div
        ref={listRef}
        {...(float ? { role: 'listbox', 'aria-label': ariaLabel ?? label } : {})}
        className={float ? 'flex flex-col gap-0.5 self-stretch overflow-y-auto min-w-0 p-1 overscroll-contain [scrollbar-gutter:stable] scroll-py-1.5' : 'self-stretch overflow-y-auto min-w-0'}
        style={{ maxHeight: menuRect ? Math.max(80, menuRect.maxHeight - (float ? 16 + (searchable ? 48 : 0) : 56)) : undefined }}
      >
        {showGroups ? renderGroupedOptions() : (
          filteredOpts.length === 0 ? (
            <div className="px-3 py-3 text-xs text-on-surface-variant text-center italic">
              Keine Treffer
            </div>
          ) : (
            filteredOpts.map(o => renderOptionButton(o))
          )
        )}
      </div>
    </>
  )

  const portalMenu = isOpen && effectiveUsePortal ? (
    <div
      ref={menuRef}
      className={menuShellClass}
      onFocus={() => { focusWasInMenu.current = true }}
      onKeyDown={float ? menuTaste : onMenuKey}
      style={
        menuRect
          ? {
              position: 'fixed',
              // top XOR bottom — bottom means the menu flipped above the
              // trigger because there was no room below it.
              ...(menuRect.top !== undefined ? { top: menuRect.top } : {}),
              ...(menuRect.bottom !== undefined ? { bottom: menuRect.bottom } : {}),
              ...(menuRect.left !== undefined ? { left: menuRect.left } : {}),
              ...(menuRect.right !== undefined ? { right: menuRect.right } : {}),
              // Fit-to-content sizing — works correctly now that the shell is
              // inline-flex + items-start AND the search input uses w-0
              // min-w-full (breaks the circular intrinsic-sizing dependency
              // that previously made the menu fall back to 90vw).
              width: float ? menuRect.width : 'max-content',
              minWidth: float ? menuMinWidth : (menuMinWidth ?? 220),
              maxWidth: float ? 'calc(100vw - 16px)' : '90vw',
              maxHeight: menuRect.maxHeight,
              zIndex: effectiveMenuZIndex,
            }
          : float ? { position: 'fixed' as const, top: 0, left: 0, visibility: 'hidden' as const } : { visibility: 'hidden' as const }
      }
    >
      {renderOptionList()}
    </div>
  ) : null

  // In-flow path: absolute positioning, NO top/left coords. The element
  // takes its "static position" (where it would be in flow), which lands
  // it directly below the trigger by virtue of the relative parent's
  // block layout. Pair with a fixed width via the inline style below.
  const inFlowMenuPosition = 'absolute z-50'

  const valueContent = current?.leading ? (
    <span className="flex min-w-0 items-center gap-1.5">
      <span className="shrink-0 inline-flex items-center">{current.leading}</span>
      <span className="truncate">{current.label}</span>
    </span>
  ) : (
    <span className={`truncate ${current ? '' : 'text-on-surface-variant'}`}>{current?.label ?? placeholder}</span>
  )

  return (
    <div className={size === 'compact' ? '' : 'space-y-1.5'}>
      {label && (
        <label className="block text-[10px] font-medium uppercase tracking-wider text-on-surface-variant">{label}</label>
      )}
      <div className="relative" ref={wrapRef}>
        <button
          ref={triggerRef}
          type="button"
          disabled={disabled}
          onClick={() => setIsOpen(v => !v)}
          aria-label={ariaLabel}
          aria-expanded={isOpen}
          aria-invalid={invalid || undefined}
          {...(float ? { 'aria-haspopup': 'listbox' as const } : {})}
          onKeyDown={float ? triggerTaste : onTriggerKey}
          className={triggerCls}
        >
          {icon ? (
            <span className="flex min-w-0 items-center gap-2">
              <span className={`material-symbols-outlined shrink-0 text-primary ${size === 'touch' ? '!text-[22px]' : '!text-[16px]'}`} aria-hidden="true">{icon}</span>
              {valueContent}
            </span>
          ) : valueContent}
          <span className={`material-symbols-outlined shrink-0 transition-transform ${size === 'touch' ? '!text-[24px] text-primary' : '!text-[16px] text-on-surface-variant'} ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true">
            expand_more
          </span>
        </button>
        {isOpen && !effectiveUsePortal && (
          <div
            ref={menuRef}
            className={`${inFlowMenuPosition} ${menuShellClass}`}
            onFocus={() => { focusWasInMenu.current = true }}
            onKeyDown={onMenuKey}
            style={{ width: menuMinWidth ?? 320 }}
          >
            {renderOptionList()}
          </div>
        )}
        {portalMenu && createPortal(portalMenu, document.body)}
      </div>
    </div>
  )
}
